import { Box, CircularProgress, Typography } from '@mui/material';
import { getPageStatus, PageLayout } from 'components/PageLayout';
import { useProvisionalSubmission } from 'features/provisionalSubmissions/hooks/useProvisionalSubmission';
import {
  CreateReportProvisionalSubmissionResponse,
  EditGeoEventProvisionalSubmissionResponse,
  EditLocationProvisionalSubmissionResponse,
  ProvisionalSubmissionResponse,
  ProvisionalSubmissionType,
} from 'features/provisionalSubmissions/provisionalSubmissions.types';
import {
  BaseCreateUpdateData,
  useGeoEvent,
} from 'features/geoEvents/hooks/useGeoEvent';
import { Wildfire } from 'features/wildfires/wildfire.types';
import {
  wildfireDataPatchConfigs,
  wildfireFieldPatchConfigs,
} from 'features/wildfires/hooks/useWildfireGeoEvent';
import { ReportForm } from 'features/reports/components/ReportForm';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { useAuthState } from 'state/useAuthState';
import { GeoEventPatchConfigs } from 'features/reports/components/ReportForm/types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { InlineStatus } from 'components/InlineStatus';
import { LocationForm } from 'app/pages/CreateEditLocation/LocationForm/LocationForm';
import { Location } from 'features/locations/location.types';
import { GeoEventTypes } from '../../../constants';
import { WildfireForm } from '../CreateEditWildfire/WildfireForm/WildfireForm';
import { EditAutomatedGeoEventForm } from '../EditAutomatedGeoEvent/EditAutomatedGeoEventForm';

const getPatchConfigs = (geoEventType: GeoEventType): GeoEventPatchConfigs => {
  if (geoEventType === GeoEventTypes.wildfire) {
    return {
      dataPatchConfigs: wildfireDataPatchConfigs,
      fieldPatchConfigs: wildfireFieldPatchConfigs,
    };
  }

  throw new Error(`Unsupported geoEventType for review: ${geoEventType}`);
};

type WithGeoEventProps<T extends BaseCreateUpdateData> = {
  id: number;
  expectedType: GeoEventType;
  render: (geoEvent: T) => JSX.Element;
};

const WithGeoEvent = <T extends BaseCreateUpdateData>({
  id,
  expectedType,
  render,
}: WithGeoEventProps<T>): JSX.Element => {
  const { t } = useTranslation();
  const { geoEvent, isGeoEventLoading, isGeoEventFetchError } = useGeoEvent<T>({
    id,
  });

  if (isGeoEventLoading) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isGeoEventFetchError || !geoEvent) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', p: 3 }}>
        <Typography>{t('reviewSubmission.loadingError')}</Typography>
      </Box>
    );
  }

  if (geoEvent.geoEventType !== expectedType) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', p: 3 }}>
        <Typography>{t('reviewSubmission.unsupportedGeoEventType')}</Typography>
      </Box>
    );
  }

  return render(geoEvent);
};

type ReviewContentProps<
  T extends ProvisionalSubmissionResponse = ProvisionalSubmissionResponse,
> = {
  submission: T;
  title: string;
};

const isEditLocationSubmission = (
  submission:
    | EditGeoEventProvisionalSubmissionResponse
    | EditLocationProvisionalSubmissionResponse,
): submission is EditLocationProvisionalSubmissionResponse =>
  submission.submittedData.geoEventType === GeoEventTypes.location;

const ReviewReportContent = ({
  submission,
  title,
}: ReviewContentProps<CreateReportProvisionalSubmissionResponse>): JSX.Element => (
  <WithGeoEvent<Wildfire>
    id={submission.submittedData.geoEventId}
    expectedType={GeoEventTypes.wildfire}
    render={(geoEvent) => (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <ReportForm
          geoEvent={geoEvent}
          geoEventPatchConfigs={getPatchConfigs(geoEvent.geoEventType)}
          getViewPath={(id) => `/i/${id}`}
          reviewSubmission={submission}
        />
      </>
    )}
  />
);

const ReviewEditGeoEventContent = ({
  submission,
  title,
}: ReviewContentProps<EditGeoEventProvisionalSubmissionResponse>): JSX.Element => (
  <WithGeoEvent<Wildfire>
    id={submission.geoEventId}
    expectedType={GeoEventTypes.wildfire}
    render={(geoEvent) => (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        {geoEvent.reporterManaged ? (
          <WildfireForm wildfire={geoEvent} reviewSubmission={submission} />
        ) : (
          <EditAutomatedGeoEventForm
            geoEvent={geoEvent}
            reviewSubmission={submission}
          />
        )}
      </>
    )}
  />
);

const ReviewEditLocationContent = ({
  submission,
  title,
}: ReviewContentProps<EditLocationProvisionalSubmissionResponse>): JSX.Element => (
  <WithGeoEvent<Location>
    id={submission.geoEventId}
    expectedType={GeoEventTypes.location}
    render={(geoEvent) => (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <LocationForm location={geoEvent} submissionUnderReview={submission} />
      </>
    )}
  />
);

const ReviewContent = (props: ReviewContentProps): JSX.Element => {
  const { submission, title } = props;
  const { t } = useTranslation();

  if (submission.submissionType === ProvisionalSubmissionType.CreateGeoEvent) {
    const { geoEventType } = submission.submittedData;

    if (geoEventType === GeoEventTypes.wildfire) {
      return (
        <>
          <Helmet>
            <title>{title}</title>
          </Helmet>
          <WildfireForm reviewSubmission={submission} />
        </>
      );
    }

    if (geoEventType === GeoEventTypes.location) {
      return (
        <>
          <Helmet>
            <title>{title}</title>
          </Helmet>
          <LocationForm submissionUnderReview={submission} />
        </>
      );
    }

    return (
      <InlineStatus
        status="error"
        fullWidth
        errorMessage={t('reviewSubmission.unsupportedGeoEventType')}
      />
    );
  }

  if (submission.submissionType === ProvisionalSubmissionType.EditGeoEvent) {
    if (isEditLocationSubmission(submission)) {
      return (
        <ReviewEditLocationContent submission={submission} title={title} />
      );
    }
    return <ReviewEditGeoEventContent submission={submission} title={title} />;
  }

  if (submission.submissionType === ProvisionalSubmissionType.CreateReport) {
    return <ReviewReportContent submission={submission} title={title} />;
  }

  return (
    <InlineStatus
      status="error"
      fullWidth
      errorMessage={t('reviewSubmission.loadingError')}
    />
  );
};

export const ReviewSubmission = (
  props: RouteComponentProps<{ id: string }>,
): JSX.Element => {
  const {
    match: {
      params: { id },
    },
  } = props;
  const { t } = useTranslation();
  const {
    permissions: { isApprover },
  } = useAuthState();

  const {
    data: submission,
    isLoading,
    isError,
  } = useProvisionalSubmission(Number(id));

  const title = t('reviewSubmission.header');

  const isAlreadyProcessed = submission && submission.status !== 'pending';

  const status = getPageStatus({
    isUnauthorized: !isApprover,
    isLoading,
    isError: isError || !submission || isAlreadyProcessed,
  });

  const errorMessage = isAlreadyProcessed
    ? t('reviewSubmission.alreadyProcessed')
    : t('reviewSubmission.loadingError');

  return (
    <PageLayout
      data-cname="ReviewSubmission"
      status={status}
      headerBarProps={{ title, action: 'back' }}
      errorMessage={status === 'error' ? errorMessage : undefined}
    >
      {submission && submission.status === 'pending' && (
        <ReviewContent submission={submission} title={title} />
      )}
    </PageLayout>
  );
};
